<template>
  <div class="cloudflare-demo">
    <h1>Sleep Content Media Gallery</h1>
    
    <div class="media-filters">
      <div class="filter-group">
        <label>Filter by type:</label>
        <select v-model="selectedType">
          <option value="">All Types</option>
          <option value="bedtime">Bedtime Stories</option>
          <option value="breathing">Breathing Exercises</option>
          <option value="brain_programming">Brain Programming</option>
          <option value="sleep_frequency">Sleep Frequency</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label>Display mode:</label>
        <select v-model="displayMode">
          <option value="grid">Grid</option>
          <option value="list">List</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label>Language:</label>
        <button 
          class="lang-button"
          :class="{ active: !useArabic }"
          @click="useArabic = false"
        >English</button>
        <button 
          class="lang-button"
          :class="{ active: useArabic }"
          @click="useArabic = true"
        >Arabic</button>
      </div>
    </div>

    <div class="items-container" :class="displayMode">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading content...</p>
      </div>
      
      <!-- Empty state -->
      <div v-else-if="filteredItems.length === 0" class="empty-state">
        <p>No sleep content found.</p>
      </div>
      
      <!-- Content grid -->
      <div 
        v-else
        v-for="item in filteredItems" 
        :key="item.id"
        class="media-item"
      >
        <div class="item-media">
          <SleepMediaViewer
            :item="item"
            :muted="true"
            :autoplay="false"
            :showControls="true"
            :useArabic="useArabic"
            :fit="'cover'"
          />
          
          <div class="media-type-badge" :class="item.type">
            {{ getTypeName(item.type) }}
          </div>
        </div>
        
        <div class="item-info">
          <h3>{{ useArabic && item.title_ar ? item.title_ar : item.title }}</h3>
          <div class="item-meta">
            <span class="duration">
              {{ formatDuration(item.duration) }}
            </span>
            <span v-if="item.display_type" class="display-type">
              {{ item.display_type === 'video' ? 'Video' : 'Image' }}
            </span>
          </div>
          
          <div v-if="displayMode === 'list'" class="item-details" v-html="useArabic && item.details_ar ? item.details_ar : item.details"></div>
          
          <div class="item-actions">
            <button class="media-action" @click="previewFullItem(item)">Preview</button>
            <button class="media-action" @click="toggleDetails(item)">Details</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Full item preview modal -->
    <div v-if="fullPreviewItem" class="modal-overlay" @click.self="fullPreviewItem = null">
      <div class="modal-content">
        <button class="modal-close" @click="fullPreviewItem = null">×</button>
        
        <h2>{{ useArabic && fullPreviewItem.title_ar ? fullPreviewItem.title_ar : fullPreviewItem.title }}</h2>
        
        <div class="modal-media">
          <!-- Use CloudflareR2ObjectViewer for a comprehensive preview with more controls -->
          <div v-if="isVideoContent(fullPreviewItem)" class="video-preview">
            <CloudflareVideoPreview
              :src="getVideoSource(fullPreviewItem)"
              :poster="fullPreviewItem.cover_photo || fullPreviewItem.main_photo"
              :muted="false"
              :autoplay="false"
              :showControls="true"
            />
          </div>
          <div v-else-if="isAudioContent(fullPreviewItem)" class="audio-preview">
            <CloudflareImagePreview
              :src="fullPreviewItem.cover_photo || fullPreviewItem.main_photo"
              :alt="fullPreviewItem.title"
              :fit="'contain'"
            />
            <div class="audio-player">
              <audio 
                :src="fullPreviewItem.audio_url || fullPreviewItem.audio_url_ar" 
                controls
                autoplay
                preload="auto"
              ></audio>
            </div>
          </div>
          <div v-else class="image-preview">
            <CloudflareImagePreview
              :src="fullPreviewItem.cover_photo || fullPreviewItem.main_photo"
              :alt="fullPreviewItem.title"
              :fit="'contain'"
              :showMetadata="true"
            />
          </div>
        </div>
        
        <div class="modal-details">
          <div v-html="useArabic && fullPreviewItem.details_ar ? fullPreviewItem.details_ar : fullPreviewItem.details"></div>
          
          <h4>Media Sources:</h4>
          <ul>
            <li v-if="fullPreviewItem.cover_photo">
              Cover Photo: <a :href="fullPreviewItem.cover_photo" target="_blank">{{ getFilename(fullPreviewItem.cover_photo) }}</a>
            </li>
            <li v-if="fullPreviewItem.main_photo">
              Main Photo: <a :href="fullPreviewItem.main_photo" target="_blank">{{ getFilename(fullPreviewItem.main_photo) }}</a>
            </li>
            <li v-if="fullPreviewItem.audio_url">
              Audio: <a :href="fullPreviewItem.audio_url" target="_blank">{{ getFilename(fullPreviewItem.audio_url) }}</a>
            </li>
            <li v-if="fullPreviewItem.video_over">
              Video: <a :href="fullPreviewItem.video_over" target="_blank">{{ getFilename(fullPreviewItem.video_over) }}</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { SleepMediaViewer, CloudflareImagePreview, CloudflareVideoPreview } from '@/components/media';

export default {
  name: 'CloudflareMediaDemo',
  components: {
    SleepMediaViewer,
    CloudflareImagePreview,
    CloudflareVideoPreview
  },
  setup() {
    // State
    const items = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedType = ref('');
    const displayMode = ref('grid');
    const useArabic = ref(false);
    const fullPreviewItem = ref(null);
    
    // Sample API response
    const apiResponse = {
      "success": true,
      "data": {
        "current_page": 1,
        "data": [
          {
            "id": 1,
            "title": "Guess What.. They got divorced!",
            "title_ar": "احزرو مين اتطلق",
            "audio_url": "https://prodresources.ams3.cdn.digitaloceanspaces.com/Bedtime%20Stories/EP%2001%20FINAL%20MIX%20(latest%20edits).wav",
            "audio_url_ar": null,
            "voice_over": null,
            "voice_over_ar": null,
            "video_over": null,
            "video_over_ar": null,
            "cover_photo": "https://eu1.kewaarcdn.com/sleep_1_cover_photo_21_main_photo_Kewaar-Bedtime-Ep1.jpg",
            "cover_photo_ar": null,
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/sleep_1_main_photo_1_main_photo_Kewaar-Bedtime-Ep1.jpg",
            "details": "<p style=\"text-align: left\">Hassna and Jamila will take you into a journey of their simple lifestyle in the village, where their daily catching up drifts into neighborhood gossips. It's rainy, foggy, and dark outside. Inside, our two ladies are cocooned next to the heat of the fireplace. You will disconnect from your worries and dive into a world of comfort and familiarity, of safety and nostalgia, of laughter and home. Doze off into a peaceful snooze and listen to Jamila and Hassna share their juicy stories. Let their tales wrap you in warmth and gently guide you to your sleep.</p>",
            "details_ar": "<p style=\"text-align: left\">ستأخذك حسناء وجميلة في رحلةٍ إلى عالمهما البسيط في القرية، حيث يتحول حديثهما اليومي إلى همساتٍ عن أخبار الجيران.</p><p style=\"text-align: left\"><br class=\"hard-break\"></p><p style=\"text-align: left\">&nbsp;في الخارج، المطر يتساقط، الضباب يلفّ المكان، والليل حالك. أما في الداخل، فتجلس الصديقتان قرب دفء الموقدة.</p><p style=\"text-align: left\"><br class=\"hard-break\"></p><p style=\"text-align: left\">أغمض عينيك، ودع النعاس يتسلل إليك برفق، ويأخذك حديثهما نحو عالمٍ من الطمأنينة والحنين، ويدخلك في غفوة هانئة.</p>",
            "type": "bedtime",
            "display_type": "still",
            "created_at": "2025-02-06T19:55:12.000000Z",
            "updated_at": "2025-06-08T16:24:06.000000Z"
          },
          {
            "id": 2,
            "title": "The Price of Freedom",
            "title_ar": "رجع لأصلو",
            "audio_url": "https://eu1.kewaarcdn.com/2_audio_url_02_final_mix__latest_edit_.wav",
            "audio_url_ar": null,
            "voice_over": null,
            "voice_over_ar": null,
            "video_over": null,
            "video_over_ar": null,
            "cover_photo": "https://eu1.kewaarcdn.com/2_cover_photo_Kewaar-Bedtime-Ep2.jpg",
            "cover_photo_ar": null,
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/2_main_photo_Kewaar-Bedtime-Ep2.jpg",
            "details": "<p>Hassna and Jamila will take you into a journey of their simple lifestyle in the village, where their daily catching up drifts into neighborhood gossips. With the soft rays of a gentle sun coming in through the window, you will disconnect from your worries and dive into a world of comfort and familiarity, of safety and nostalgia, of laughter and home. The rain is soft, the weather is warm, and the room smells like coffee and cardamom. Doze off into a peaceful snooze as you listen to Jamila and Hassna share their juicy stories. Let their tales wrap you in warmth and gently guide you to your sleep.11</p>",
            "details_ar": "<p>\"ستأخذك حسناء وجميلة في رحلةٍ إلى عالمهما البسيط في القرية، حيث يتحول حديثهما اليومي إلى همساتٍ عن أخبار الجيران.\n\nفي هذا الجو الخريفي الصباحي حيث أشعة الشمس الدافئة تمتزج مع نسمة الهواء الباردة تجلس الصديقتان في مكانهما المعتاد قرب النافذة تحتسيان رشفات القهوة.\n\nأغمض عينيك، ودع النعاس يتسلل إليك برفق، ويأخذك حديثهما نحو عالمٍ من الطمأنينة والحنين، ويدخلك في غفوة هانئة.\"</p>",
            "type": "bedtime",
            "display_type": "still",
            "created_at": "2025-02-06T19:56:49.000000Z",
            "updated_at": "2025-06-11T11:14:23.000000Z"
          },
          {
            "id": 7,
            "title": "Bp on Deep Sleep",
            "title_ar": "برمجة العقل على النوم العميق",
            "audio_url": "https://eu1.kewaarcdn.com/7_cover_photo_02-DeepSleep-OussamaAttar-Audio-EN.jpg",
            "audio_url_ar": "https://prodresources.ams3.cdn.digitaloceanspaces.com/Audios1/Audios/Oussama%20Attar/02-%20Deep%20Sleep%20-%20Oussama%20Attar.mp4",
            "voice_over": "https://eu1.kewaarcdn.com/7_cover_photo_02-DeepSleep-OussamaAttar-Audio-EN.jpg",
            "voice_over_ar": null,
            "video_over": "https://eu1.kewaarcdn.com/7_cover_photo_02-DeepSleep-OussamaAttar-Audio-EN.jpg",
            "video_over_ar": null,
            "cover_photo": "https://eu1.kewaarcdn.com/7_cover_photo_02-DeepSleep-OussamaAttar-Audio-EN.jpg",
            "cover_photo_ar": "https://eu1.kewaarcdn.com/7_cover_photo_ar_02-DeepSleep-OussamaAttar-Audio-AR.jpg",
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/7_main_photo_02-DeepSleep-OussamaAttar-Audio-EN.jpg",
            "details": "<p style=\"text-align: left\">Bp on Deep sleep1</p>",
            "details_ar": "برمجة العقل على النوم العميق",
            "type": "brain_programming",
            "display_type": "still",
            "created_at": "2025-02-18T22:54:14.000000Z",
            "updated_at": "2025-05-22T08:36:41.000000Z"
          },
          {
            "id": 8,
            "title": "Sleep",
            "title_ar": "النوم",
            "audio_url": "https://eu1.kewaarcdn.com/8_video_over_Sleep-EN.mp4",
            "audio_url_ar": "https://eu1.kewaarcdn.com/8_audio_url_ar_Sleep-EN.mp4",
            "voice_over": "https://eu1.kewaarcdn.com/8_voice_over_Sleep-EN.mp4",
            "voice_over_ar": null,
            "video_over": "https://eu1.kewaarcdn.com/8_video_over_Sleep-EN.mp4",
            "video_over_ar": "https://eu1.kewaarcdn.com/8_video_over_ar_Sleep-AR.mp4",
            "cover_photo": "https://eu1.kewaarcdn.com/PHOTO-2025-03-07-23-20-18.jpg",
            "cover_photo_ar": "https://eu1.kewaarcdn.com/8_cover_photo_ar_4jofsB2M9E-Sleep_Cover_-AR.jpg",
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/8_main_photo_WhatsApp_Image_2025-02-07_at_01.37.25__1_.jpeg",
            "details": "<p style=\"text-align: left\">Sleep</p>",
            "details_ar": "<p>sleep</p>",
            "type": "breathing",
            "display_type": "video",
            "created_at": "2025-02-06T21:40:21.000000Z",
            "updated_at": "2025-06-08T16:24:42.000000Z"
          },
          {
            "id": 9,
            "title": "Anxiety",
            "title_ar": "القلق",
            "audio_url": "https://eu1.kewaarcdn.com/9_video_over_Anxiety-EN.mp4",
            "audio_url_ar": "https://eu1.kewaarcdn.com/9_audio_url_ar_Anxiety-EN.mp4",
            "voice_over": "https://eu1.kewaarcdn.com/9_video_over_Anxiety-EN.mp4",
            "voice_over_ar": null,
            "video_over": "https://eu1.kewaarcdn.com/9_video_over_Anxiety-EN.mp4",
            "video_over_ar": "https://eu1.kewaarcdn.com/9_video_over_ar_Anxiety-EN.mp4",
            "cover_photo": "https://eu1.kewaarcdn.com/PHOTO-2025-03-07-23-20-17.jpg",
            "cover_photo_ar": "https://eu1.kewaarcdn.com/9_cover_photo_ar_4jofkX6i5N-Anxiety_Cover_-AR.jpg",
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/9_main_photo_WhatsApp_Image_2025-02-07_at_01.37.24__1_.jpeg",
            "details": "<p style=\"text-align: left\">Anxiety</p>",
            "details_ar": "<p style=\"text-align: left\">anxiety</p><p style=\"text-align: left\"></p>",
            "type": "breathing",
            "display_type": "video",
            "created_at": "2025-02-06T21:40:21.000000Z",
            "updated_at": "2025-06-02T12:32:42.000000Z"
          },
          {
            "id": 10,
            "title": "Recharge",
            "title_ar": "استعد طاقتك",
            "audio_url": "https://eu1.kewaarcdn.com/10_audio_url_Recharge-EN.mp4",
            "audio_url_ar": "https://eu1.kewaarcdn.com/10_audio_url_ar_Recharge-EN.mp4",
            "voice_over": "https://eu1.kewaarcdn.com/10_voice_over_Recharge-EN.mp4",
            "voice_over_ar": null,
            "video_over": "https://eu1.kewaarcdn.com/10_video_over_Recharge-EN.mp4",
            "video_over_ar": "https://eu1.kewaarcdn.com/10_video_over_ar_Recharge-AR.mp4",
            "cover_photo": "https://eu1.kewaarcdn.com/PHOTO-2025-03-07-23-20-17%202.jpg",
            "cover_photo_ar": "https://eu1.kewaarcdn.com/10_main_photo_WhatsApp_Image_2025-02-07_at_01.37.25__1_.jpeg",
            "duration": 1,
            "main_photo": "https://eu1.kewaarcdn.com/10_main_photo_WhatsApp_Image_2025-02-07_at_01.37.25__1_.jpeg",
            "details": "Recharge",
            "details_ar": "recharge",
            "type": "breathing",
            "display_type": "video",
            "created_at": "2025-02-06T21:42:30.000000Z",
            "updated_at": "2025-06-02T12:36:23.000000Z"
          }
        ],
        "first_page_url": "http://api.kewaar.com/api/admin/sleep1?page=1",
        "from": 1,
        "last_page": 3,
        "last_page_url": "http://api.kewaar.com/api/admin/sleep1?page=3",
        "links": [
          {
            "url": null,
            "label": "&laquo; Previous",
            "active": false
          },
          {
            "url": "http://api.kewaar.com/api/admin/sleep1?page=1",
            "label": "1",
            "active": true
          },
          {
            "url": "http://api.kewaar.com/api/admin/sleep1?page=2",
            "label": "2",
            "active": false
          },
          {
            "url": "http://api.kewaar.com/api/admin/sleep1?page=3",
            "label": "3",
            "active": false
          },
          {
            "url": "http://api.kewaar.com/api/admin/sleep1?page=2",
            "label": "Next &raquo;",
            "active": false
          }
        ],
        "next_page_url": "http://api.kewaar.com/api/admin/sleep1?page=2",
        "path": "http://api.kewaar.com/api/admin/sleep1",
        "per_page": 10,
        "prev_page_url": null,
        "to": 10,
        "total": 29
      },
      "message": "Sleep content retrieved successfully"
    };
    
    // Process data to ensure proper display types
    const processItems = (data) => {
      if (!data || !data.data || !Array.isArray(data.data.data)) {
        return [];
      }
      
      return data.data.data.map(item => {
        // Determine display_type if not already set
        if (!item.display_type) {
          if (item.video_over || item.video_over_ar) {
            item.display_type = 'video';
          } else {
            item.display_type = 'still';
          }
        }
        
        // Ensure there's always a main_photo or cover_photo if available
        if (!item.main_photo && item.cover_photo) {
          item.main_photo = item.cover_photo;
        }
        
        // Ensure duration value is a number
        if (item.duration) {
          item.duration = Number(item.duration);
        }
        
        return item;
      });
    };
    
    // Filtered items based on selected type
    const filteredItems = computed(() => {
      if (!selectedType.value) {
        return items.value;
      }
      return items.value.filter(item => item.type === selectedType.value);
    });
    
    // Format duration for display
    const formatDuration = (seconds) => {
      if (!seconds || seconds <= 1) return '< 1 sec';
      
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      
      if (minutes === 0) {
        return `${remainingSeconds} sec`;
      }
      
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    
    // Get readable type name
    const getTypeName = (type) => {
      const typeNames = {
        'bedtime': 'Bedtime Story',
        'breathing': 'Breathing Exercise',
        'sleep_frequency': 'Sleep Frequency',
        'souds_of_nature': 'Sounds of Nature',
        'brain_programming': 'Brain Programming'
      };
      return typeNames[type] || type;
    };
    
    // Get filename from URL
    const getFilename = (url) => {
      if (!url) return '';
      const parts = url.split('/');
      return parts[parts.length - 1];
    };
    
    // Preview full item
    const previewFullItem = (item) => {
      fullPreviewItem.value = item;
    };
    
    // Toggle details for an item
    const toggleDetails = (item) => {
      fullPreviewItem.value = item;
    };
    
    // Check if item has video content
    const isVideoContent = (item) => {
      return item.display_type === 'video' || 
             (item.video_over || item.video_over_ar);
    };
    
    // Check if item has audio content
    const isAudioContent = (item) => {
      return item.type === 'bedtime' || 
             ((item.audio_url || item.audio_url_ar) && !isVideoContent(item));
    };
    
    // Get appropriate video source
    const getVideoSource = (item) => {
      if (useArabic.value && item.video_over_ar) {
        return item.video_over_ar;
      }
      return item.video_over || item.audio_url;
    };
    
    // Load data from API
    onMounted(() => {
      // In a real application, we would fetch from an API
      // fetch('https://api.kewaar.com/api/admin/sleep1')
      //   .then(response => response.json())
      //   .then(data => {
      //     items.value = processItems(data);
      //     loading.value = false;
      //   })
      //   .catch(err => {
      //     error.value = err.message;
      //     loading.value = false;
      //   });
      
      // For demo purposes, use the sample API response
      setTimeout(() => {
        items.value = processItems(apiResponse);
        loading.value = false;
      }, 1000);
    });
    
    return {
      items,
      loading,
      error,
      selectedType,
      displayMode,
      useArabic,
      filteredItems,
      fullPreviewItem,
      formatDuration,
      getTypeName,
      getFilename,
      previewFullItem,
      toggleDetails,
      isVideoContent,
      isAudioContent,
      getVideoSource
    };
  }
}
</script>

<style scoped>
.cloudflare-demo {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
}

h1 {
  font-size: 24px;
  margin-bottom: 24px;
  color: #1f2937;
}

.media-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
}

select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: white;
  font-size: 14px;
}

.lang-button {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: white;
  font-size: 14px;
  cursor: pointer;
}

.lang-button.active {
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;
}

.items-container {
  display: grid;
  gap: 24px;
}

/* Grid layout */
.items-container.grid {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

/* List layout */
.items-container.list .media-item {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 20px;
}

.media-item {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.media-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.item-media {
  position: relative;
  aspect-ratio: 16 / 9;
  max-height: 220px;
}

.items-container.list .item-media {
  height: 100%;
}

.media-type-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: rgba(0,0,0,0.6);
}

.media-type-badge.bedtime {
  background-color: #8b5cf6;
}

.media-type-badge.breathing {
  background-color: #06b6d4;
}

.media-type-badge.brain_programming {
  background-color: #ef4444;
}

.media-type-badge.sleep_frequency {
  background-color: #f59e0b;
}

.item-info {
  padding: 16px;
}

.item-info h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.item-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.duration, .display-type {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
}

.duration::before {
  content: "⏱";
  margin-right: 4px;
}

.display-type::before {
  content: "📺";
  margin-right: 4px;
}

.item-details {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 16px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;  
  overflow: hidden;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.media-action {
  padding: 6px 12px;
  border-radius: 6px;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.media-action:hover {
  background-color: #e5e7eb;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  grid-column: 1 / -1;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  grid-column: 1 / -1;
  background-color: #f9fafb;
  border-radius: 8px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  z-index: 10;
}

.modal-content h2 {
  margin: 0;
  padding: 20px;
  font-size: 20px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
}

.modal-media {
  padding: 0;
}

.modal-details {
  padding: 20px;
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
}

.modal-details h4 {
  margin-top: 24px;
  margin-bottom: 12px;
  color: #1f2937;
}

.modal-details ul {
  margin: 0;
  padding-left: 20px;
}

.modal-details li {
  margin-bottom: 8px;
}

.modal-details a {
  color: #2563eb;
  text-decoration: none;
}

.video-preview, .audio-preview, .image-preview {
  width: 100%;
}

.audio-preview {
  position: relative;
}

.audio-preview .audio-player {
  padding: 12px;
  background-color: #f3f4f6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .items-container.list .media-item {
    grid-template-columns: 1fr;
  }
  
  .items-container.list .item-media {
    max-height: 220px;
  }
  
  .modal-content {
    width: 95%;
  }
}
</style> 